<template>
  <div>
    <grading-table
      :markable-questions="markableQuestions"
      :state="gradingSet.grades"
      :competition="competition"
      :original-grading="gradingSet.original"
      :is-locked="isLocked"
    />
    <div
      v-if="gradingStatus === 'initial' || cannotGrade"
      class="md:flex items-baseline"
    >
      <div class="flex-1">
        <label
          for="suitable_for_interview"
          class="text-xl md:text-2xl font-bold cursor-pointer"
        >
          Do you believe that this project is suitable for interview?
        </label>
      </div>
      <div class="mt-6 md:ml-4 md:mt-0">
        <input
          id="suitable_for_interview"
          v-model="grading.suitable_for_interview"
          type="checkbox"
          class="hidden"
          :disabled="isLocked"
          name="suitable_for_interview"
        />
        <div
          class="border flex uppercase font-bold overflow-hidden cursor-pointer text-center"
        >
          <div
            class="w-full py-2 px-8 m-1 outline-none"
            tabindex="0"
            :class="{
              'bg-primary-600 text-white': grading.suitable_for_interview,
              'hover:bg-primary-200 focus:bg-primary-200':
                !grading.suitable_for_interview && !isLocked,
              'cursor-not-allowed': isLocked
            }"
            @click.self="setSuitable(true)"
            @keydown.enter="setSuitable(true)"
          >
            Yes
          </div>
          <div
            class="w-full py-2 px-8 m-1 outline-none"
            tabindex="0"
            :class="{
              'bg-primary-600 text-white': !grading.suitable_for_interview,
              'hover:bg-primary-200 focus:bg-primary-200':
                grading.suitable_for_interview && !isLocked,
              'cursor-not-allowed': isLocked
            }"
            @click.self="setSuitable(false)"
            @keydown.enter="setSuitable(false)"
          >
            No
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="gradingStatus === 'interview' && !cannotGrade"
      class="md:flex items-baseline"
    >
      <div class="flex-1">
        <label
          for="suitable_for_funding"
          class="text-xl md:text-2xl font-bold cursor-pointer"
        >
          Do you believe that this project is suitable for funding?
        </label>
      </div>
      <div class="mt-6 md:ml-4 md:mt-0">
        <input
          id="suitable_for_funding"
          v-model="grading.suitable_for_funding"
          type="checkbox"
          class="hidden"
          :disabled="isLocked"
          name="suitable_for_funding"
        />
        <div
          class="border flex uppercase font-bold overflow-hidden cursor-pointer text-center"
        >
          <div
            class="w-full py-2 px-8 m-1 outline-none"
            tabindex="0"
            :class="{
              'bg-primary-600 text-white': grading.suitable_for_funding,
              'hover:bg-primary-200 focus:bg-primary-200':
                !grading.suitable_for_funding && !isLocked,
              'cursor-not-allowed': isLocked
            }"
            @click.self="setSuitable(true)"
            @keydown.enter="setSuitable(true)"
          >
            Yes
          </div>
          <div
            class="w-full py-2 px-8 m-1 outline-none"
            tabindex="0"
            :class="{
              'bg-primary-600 text-white': !grading.suitable_for_funding,
              'hover:bg-primary-200 focus:bg-primary-200':
                grading.suitable_for_funding && !isLocked,
              'cursor-not-allowed': isLocked
            }"
            @click.self="setSuitable(false)"
            @keydown.enter="setSuitable(false)"
          >
            No
          </div>
        </div>
      </div>
    </div>
    <content-section
      v-if="gradingStatus === 'interview' && !cannotGrade"
      class="form-section mt-12"
    >
      <template #title>
        <h2>
          {{
            singleStageGrading
              ? "Feedback for applicant"
              : "Interview feedback for applicant"
          }}
        </h2>
        <div class="italic text-sm text-grey-700">
          <p>
            These comments will be made available to the applicant after the
            {{ singleStageGrading ? "grading" : "interview" }} phase. Please
            make sure that you are as constructive as possible, highlighting the
            strengths and weaknesses of the bid.
          </p>
        </div>
      </template>

      <div class="-mx-6 flex flex-wrap -mb-8">
        <input-multiline
          :input="{
            key: 'interview_feedback',
            type: 'text',
            label: 'Interview Feedback',
            instructions: null,
            attributes: {
              placeholder: null,
              default: null,
              prepend: null,
              append: null,
              locked: isLocked,
              required: true
            },
            wrapper: {
              width: 'full'
            },
            validation: {
              min: false,
              max: false,
              limit: 300
            }
          }"
          :application-value="grading.interview_feedback"
          @input="
            e => {
              grading['interview_feedback'] = e.target.value.trim();
            }
          "
        />
      </div>
    </content-section>
    <content-section v-if="!singleStageGrading" class="form-section mt-12">
      <template #title>
        <h2>Feedback for applicant</h2>
        <div class="italic text-sm text-grey-700">
          <p>
            These comments will be made available to the applicant prior to the
            interview phase. Please make sure that you are as constructive as
            possible, highlighting the strengths and weaknesses of the bid.
          </p>
          <p>
            Please include any issues which the applicant will need to address
            at interview.
          </p>
        </div>
      </template>

      <div class="-mx-6 flex flex-wrap -mb-8">
        <input-multiline
          :input="{
            key: 'comments',
            type: 'text',
            label: 'Application feedback',
            instructions: null,
            attributes: {
              placeholder: null,
              default: null,
              prepend: null,
              append: null,
              locked: isLocked || gradingStatus === 'interview',
              required: true
            },
            wrapper: {
              width: 'full'
            },
            validation: {
              min: false,
              max: false,
              limit: 300
            }
          }"
          :application-value="grading.comments"
          @input="
            e => {
              grading['comments'] = e.target.value.trim();
            }
          "
        />
      </div>
    </content-section>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
export default {
  name: "ApplicationGrade",
  props: {
    assessment: {
      type: Array,
      default: null
    },
    isLocked: {
      type: Boolean,
      default: true
    },
    grades: {
      type: Object,
      default: null
    },
    project: {
      type: String,
      required: true
    },
    projectStatusDates: {
      type: Object,
      default: null
    },
    gradingStatus: {
      type: String,
      required: true
    },
    competition: {
      type: Object,
      required: true
    },
    user: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isWebsocketUpdate: false,
      grading: {
        suitable_for_interview: false,
        suitable_for_funding: false,
        initial_grades: {},
        interview_grades: {},
        comments: "",
        interview_feedback: ""
      }
    };
  },
  computed: {
    markableQuestions() {
      return this.assessment.reduce((arr, { fields }) => {
        const markable = fields.reduce(
          (canMark, { name, key, label, markable }, i) => {
            if (!markable) return canMark;
            canMark.push({
              key,
              label: name ? `${i + 1}. ${name}` : label
            });
            return canMark;
          },
          []
        );
        arr.push(...markable);
        return arr;
      }, []);
    },
    gradingSet() {
      return {
        grades:
          this.gradingStatus === "interview" && !this.cannotGrade
            ? this.grading.interview_grades
            : this.grading.initial_grades,
        original:
          this.gradingStatus === "interview" && !this.cannotGrade
            ? this.grading.initial_grades
            : false
      };
    },
    gradingStatusField() {
      return `${this.gradingStatus}_grading_status`;
    },
    cannotGrade() {
      return (
        this.projectStatusDates.rejected_at &&
        this.projectStatusDates.initial_notified_at &&
        !this.projectStatusDates.interview_notified_at
      );
    },
    singleStageGrading() {
      return this.competition.single_stage_grading > 0;
    }
  },
  created() {
    this.setDefaults("initial_grades");
    this.setDefaults("interview_grades");
    this.setStatusWatchers();
    this.grading = this.grades || this.grading;
    if (!this.isLocked) {
      window.Echo.private(
        `assessment-grading.${this.project}.${this.user.id}`
      ).listen(".assessment-grading-inline-updated", ({ data }) =>
        this.webSocketUpdate(data)
      );
    }
  },
  methods: {
    setStatusWatchers() {
      const statuses = [
        "suitable_for_interview",
        "suitable_for_funding",
        "comments",
        "interview_feedback"
      ];
      statuses.forEach(status =>
        this.$watch(`grading.${status}`, () => this.updateGrades(this))
      );
    },
    assignWatchers(initial_or_interview) {
      for (let key in this.grading[initial_or_interview]) {
        this.$watch(
          `grading.${initial_or_interview}.${key}.grade`,
          this.updateGrade
        );
        this.$watch(
          `grading.${initial_or_interview}.${key}.note`,
          this.updateGrade
        );
      }
    },
    updateGrade(newValue, oldValue) {
      if (newValue === oldValue) return;
      if (this.isWebsocketUpdate) return;
      this.updateGrades(this);
    },
    updateGrades: debounce(
      self =>
        axios
          .post(route("assessments.view", self.project), self.grading)
          .then(({ data }) => {
            return data;
          }),
      500
    ),
    webSocketUpdate(requestData) {
      this.isWebsocketUpdate = true;
      axios
        .get(
          route("assessments.assessor-grades", {
            application: requestData.uuid,
            user: requestData.user_id
          })
        )
        .then(
          ({ data }) =>
            (this.grading = Object.assign(this.grading, data.grades))
        )
        .finally(() => (this.isWebsocketUpdate = false));
    },
    setSuitable(value) {
      if (this.isLocked) return;
      if (this.gradingStatus === "initial") {
        this.grading.suitable_for_interview = value;
      } else {
        this.grading.suitable_for_funding = value;
      }
    },
    setDefaults(initial_or_interview) {
      const defaultGrades = this.markableQuestions.reduce((obj, { key }) => {
        if (this.grading[initial_or_interview][key]) {
          obj[key] = this.grades[initial_or_interview][key];
        } else {
          obj[key] = {
            grade: 0,
            note: ""
          };
        }
        return obj;
      }, {});
      this.grading[initial_or_interview] = Object.assign({}, defaultGrades);
      this.assignWatchers(initial_or_interview);
    }
  }
};
</script>
